import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Component, type ReactNode } from 'react';
import { AppShell } from '@/components/Layout/AppShell';
import { ToastProvider } from '@/components/ui/toast';
import { KanbanPage } from '@/pages/KanbanPage';
import { AnalyticsPage } from '@/pages/AnalyticsPage';
import { CandidatesPage } from '@/pages/CandidatesPage';
import { UsersPage } from '@/pages/UsersPage';
import { LoginPage } from '@/pages/LoginPage';
import { SetupPage } from '@/pages/SetupPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { useAuth } from '@/hooks/useAuth';

class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null; stack: string }
> {
  state = { error: null, stack: '' };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: { componentStack: string }) {
    this.setState({ stack: info.componentStack ?? '' });
    console.error('[ErrorBoundary]', error, info);
  }
  render() {
    if (this.state.error) {
      const err = this.state.error as Error;
      return (
        <div style={{ padding: 32, fontFamily: 'monospace', backgroundColor: '#f8f7f4', minHeight: '100vh' }}>
          <p style={{ color: '#dc2626', fontWeight: 700, marginBottom: 8 }}>Erro ao carregar aplicação</p>
          <pre style={{ color: '#1c1c1a', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 16 }}>
            {err.name}: {err.message}
          </pre>
          <pre style={{ color: '#6b6860', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', borderTop: '1px solid #e2e0d8', paddingTop: 16 }}>
            {err.stack}
          </pre>
          <pre style={{ color: '#9c9890', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', borderTop: '1px solid #e2e0d8', paddingTop: 16 }}>
            {this.state.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

function Spinner() {
  return (
    <div
      className="flex min-h-screen items-center justify-center"
      style={{ backgroundColor: '#f8f7f4' }}
    >
      <div
        className="h-6 w-6 animate-spin rounded-full border-2"
        style={{ borderColor: '#e2e0d8', borderTopColor: '#5b5ef4' }}
      />
    </div>
  );
}

function ProtectedLayout() {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) return <Spinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <Outlet />;
}

export default function App() {
  return (
    <ErrorBoundary>
    <ToastProvider>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route element={<ProtectedLayout />}>
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/kanban" replace />} />
            <Route path="/kanban" element={<KanbanPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/candidates" element={<CandidatesPage />} />
            <Route path="/users" element={<UsersPage />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
    </ToastProvider>
    </ErrorBoundary>
  );
}
